import PanelError from '@wandb/weave/common/components/elements/PanelError';
// @todo: Migrate to @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {Button} from '@wandb/weave/components/Button';
import {useWeaveContext} from '@wandb/weave/context';
import {
  defaultLanguageBinding,
  isConstNode,
  isOutputNode,
  isVarNode,
  isVoidNode,
} from '@wandb/weave/core';
import {UseNodeValueServerExecutionError} from '@wandb/weave/errors';
import * as _ from 'lodash';
import React, {useCallback, useMemo, useRef, useState} from 'react';

import {ErrorBoundary} from '../../components/ErrorBoundary';
import {captureError} from '../../integrations';
import {useDatadogRum} from '../../util/datadog/datadogRum';
import {type PanelProps} from '../../util/panelProps';
import {useStablePanelId} from '../Panel/PanelIDContext';

const MAX_CONFIG_STRING_LENGTH = 2500;

const hashStr = (str?: string) => {
  // From https://stackoverflow.com/a/7616484
  let hash = 0;
  let i: number;
  let chr: number;
  if (str == null || str.length === 0) {
    return hash;
  }
  for (i = 0; i < str.length; i++) {
    chr = str.charCodeAt(i);
    hash = (hash << 5) - hash + chr;
    hash |= 0; // Convert to 32bit integer
  }
  return hash;
};

/** Coerce boundary catches to Error so Sentry gets a real exception + name. */
function weavePanelCaughtToError(caught: unknown): Error {
  if (caught instanceof UseNodeValueServerExecutionError) {
    return caught;
  }
  if (caught instanceof Error) {
    return caught;
  }
  const name = 'WeavePanelErrorBoundary';
  if (typeof caught === 'string') {
    const err = new Error(caught);
    err.name = name;
    return err;
  }
  if (
    caught != null &&
    typeof caught === 'object' &&
    typeof (caught as {message?: unknown}).message === 'string'
  ) {
    const o = caught as {message: string; error_type?: unknown};
    const err = new Error(o.message);
    err.name =
      typeof o.error_type === 'string' && o.error_type ? o.error_type : name;
    return err;
  }
  const err = new Error(String(caught));
  err.name = name;
  return err;
}

// Do not nest these components - you will get weird undo cycles.
type MinimumPanelProps<T> = Pick<PanelProps<T>, 'config' | 'updateConfig'>;

type ReversibleWeaveErrorBoundaryProps<T extends MinimumPanelProps<any>> = T & {
  innerComponent: React.ComponentType<T>;
  sourcePanel: string;
  panelConfigKey: string;
  hardResetValue?: any;
};

export const ReversibleWeaveErrorBoundary: React.FC<
  ReversibleWeaveErrorBoundaryProps<any>
> = props => {
  const {reversibleUpdateConfig, canUndo, undoUpdate, canReset, resetConfig} =
    useReversibleUpdateConfig(
      props.config,
      props.updateConfig,
      useMemo(() => props.hardResetValue ?? {}, [props.hardResetValue])
    );
  const innerProps = useMemo(() => {
    return {
      ..._.omit(props, ['innerComponent', 'sourcePanel', 'hardResetValue']),
      updateConfig: reversibleUpdateConfig,
    };
  }, [props, reversibleUpdateConfig]);
  return (
    <WeaveErrorBoundary
      canReset={canReset}
      onReset={resetConfig}
      canUndo={canUndo}
      onUndo={undoUpdate}
      errorConfig={props.config[props.panelConfigKey]}
      sourcePanel={props.sourcePanel}>
      <props.innerComponent {...innerProps} />
    </WeaveErrorBoundary>
  );
};

const trimString = (str?: string) => {
  if (str == null) {
    return '';
  }
  if (str.length > MAX_CONFIG_STRING_LENGTH) {
    return str.slice(0, MAX_CONFIG_STRING_LENGTH) + '...';
  }
  return str;
};

const minifiedConfig = (config: any): any => {
  if (config == null) {
    return '';
  } else if (isVoidNode(config)) {
    return '<VOID>';
  } else if (isConstNode(config)) {
    return `[${minifiedConfig(config.val)}]`;
  } else if (isVarNode(config)) {
    return `{{${config.varName}}}`;
  } else if (isOutputNode(config)) {
    const argKeys = Object.keys(config.fromOp.inputs);
    const prefix = minifiedConfig(config.fromOp.inputs[argKeys[0]]);
    let args = argKeys
      .slice(1)
      .map(k => minifiedConfig(config.fromOp.inputs[k]))
      .join(', ');
    if (args !== '') {
      args = `(${args})`;
    }
    return `${prefix}.${config.fromOp.name}${args}`;
  } else if (config.type != null) {
    try {
      return defaultLanguageBinding
        .printType(config)
        .replace(/[\s\r\n\t]/g, '');
    } catch (e) {
      // pass
    }
  } else if (config.autoColumns) {
    // `autoColumns is used in table state to indicate an auto table
    // the config will have a bunch of boilerplate and this is a logically
    // equivalent representation of the config
    return {
      autoColumns: true,
    };
  } else if (_.isArray(config)) {
    return config.map(minifiedConfig);
  } else if (_.isObject(config)) {
    return _.mapValues(config, (v: any) => minifiedConfig(v));
  }
  return config;
};

export const WeaveErrorBoundary: React.FC<{
  canReset: boolean;
  onReset: () => void;
  canUndo: boolean;
  onUndo: () => void;
  errorConfig: any;
  sourcePanel: string;
}> = ({
  canReset,
  onReset,
  canUndo,
  onUndo,
  errorConfig,
  sourcePanel,
  children,
}) => {
  const [key, setKey] = useState(0);
  const weave = useWeaveContext();
  const datadogRum = useDatadogRum();

  const onError = useCallback(
    (caught: Error, _info: React.ErrorInfo) => {
      const error = weavePanelCaughtToError(caught as unknown);
      let payload = {};
      try {
        payload = {
          _errorType: 'weave_panel_error_boundary',
          sourcePanel: trimString(sourcePanel),
          errorName: trimString(error.name),
          hashedErrorMessage: hashStr(error.message),
          hashedErrorStack: hashStr(error.stack),
          hashedConfig: hashStr(JSON.stringify(errorConfig)),
          // segment analytics creates a context_page_url which is supposed to
          // be the current page, but in practice, this seems to be broken
          // about 15% of the time. Adding this manually for now.
          windowLocationURL: trimString(window.location.href),
          minifiedErrorConfig: minifiedConfig(errorConfig),
          weaveContext: weave.client.debugMeta(),
          isServerError: error instanceof UseNodeValueServerExecutionError,
        };
      } catch (e) {
        // ignore - this is an error boundary, so ignore issues here.
        console.warn(e);
      }
      datadogRum.addError(error, payload);
      // useNodeValue already captureException's via captureAndThrowError before rethrowing.
      if (!(error instanceof UseNodeValueServerExecutionError)) {
        captureError(error, 'Weave panel ErrorBoundary', {
          tags: {sourcePanel: trimString(sourcePanel)},
        });
      }
    },
    [datadogRum, errorConfig, sourcePanel, weave]
  );

  const resetPanel = useCallback(() => {
    onReset();
    setKey(k => k + 1);
  }, [onReset, setKey]);

  const undoChange = useCallback(() => {
    onUndo();
    setKey(k => k + 1);
  }, [onUndo, setKey]);

  const stablePanelId = useStablePanelId();

  const renderPanelError = useCallback(() => {
    return (
      <div style={{padding: 40}}>
        <PanelError
          panelID={stablePanelId}
          panelViewType="Weave"
          message={
            <div>
              <div>
                Oops, something went wrong. If this keeps happening, message
                support@wandb.com with a link to this page
              </div>
              <div>
                {canUndo ? (
                  <Button style={{margin: 10}} onClick={undoChange}>
                    Undo Last Change
                  </Button>
                ) : canReset ? (
                  <Button style={{margin: 10}} onClick={resetPanel}>
                    Reset Panel
                  </Button>
                ) : null}
              </div>
            </div>
          }
          logErrorMessage="Oops, something went wrong. If this keeps happening, message support@wandb.com with a link to this page"
        />
      </div>
    );
  }, [canUndo, undoChange, canReset, resetPanel, stablePanelId]);

  return (
    <ErrorBoundary
      silent
      onError={onError}
      renderError={renderPanelError}
      key={key}>
      {children}
    </ErrorBoundary>
  );
};

const useReversibleUpdateConfig: <T>(
  config: T,
  updateConfig: (config: Partial<T>) => void,
  hardResetValue?: T,
  historyLength?: number
) => {
  reversibleUpdateConfig: (partialConfig: Partial<T>) => void;
  canUndo: boolean;
  undoUpdate: () => void;
  canReset: boolean;
  resetConfig: () => void;
} = (config, updateConfig, hardResetValue, historyLength = 3) => {
  type ConfigType = typeof config;
  type UndoHistoryType = Array<ConfigType | null>;
  const [originalConfig] = useState(config);
  const resetValue = useMemo(() => {
    let base: any;
    if (originalConfig !== config || hardResetValue == null) {
      base = {...originalConfig};
    } else {
      base = {...hardResetValue};
    }
    Object.keys(config).forEach(key => {
      if (!(key in base)) {
        base[key] = undefined;
      }
    });
    return base;
  }, [hardResetValue, originalConfig, config]);
  const undoHistory = useRef<UndoHistoryType>(
    Array.from({length: historyLength}, () => null)
  );
  const [undoHistoryIndex, setUndoHistoryIndex] = useState(-1);

  const reversibleUpdateConfig = useCallback(
    (partialConfig: ConfigType) => {
      const undoAction = _.pick(config, Object.keys(partialConfig));
      const nextHistoryIndex = (undoHistoryIndex + 1) % historyLength;
      undoHistory.current[nextHistoryIndex] = undoAction as any;
      setUndoHistoryIndex(nextHistoryIndex);
      updateConfig(partialConfig);
    },
    [config, updateConfig, historyLength, undoHistoryIndex]
  );

  const undoUpdate = useCallback(() => {
    const undoAction = undoHistory.current[undoHistoryIndex];
    if (undoAction) {
      updateConfig(undoAction);
      setUndoHistoryIndex((undoHistoryIndex - 1) % historyLength);
    }
  }, [
    undoHistory,
    undoHistoryIndex,
    updateConfig,
    setUndoHistoryIndex,
    historyLength,
  ]);

  const canUndo = useMemo(() => {
    return undoHistory.current[undoHistoryIndex] != null;
  }, [undoHistory, undoHistoryIndex]);

  const resetConfig = useCallback(() => {
    reversibleUpdateConfig(resetValue);
  }, [resetValue, reversibleUpdateConfig]);

  const canReset = useMemo(() => {
    return resetValue !== config;
  }, [resetValue, config]);

  return useMemo(() => {
    return {
      reversibleUpdateConfig: reversibleUpdateConfig as any,
      canUndo,
      undoUpdate,
      canReset,
      resetConfig,
    };
  }, [reversibleUpdateConfig, undoUpdate, canUndo, canReset, resetConfig]);
};
